import React, { useState, useEffect, useMemo } from 'react';
import { GenericSelect } from '../../components/GenericSelect';
import { useDataContext } from '../../context/data.js';
import translations from '../../constants/translations.json';

const numericSort = (a, b) => {
  const numA = parseInt(a.replace(/\D/g, ''), 10);
  const numB = parseInt(b.replace(/\D/g, ''), 10);
  return numA - numB;
};

const CINEMA_ROW_SEAT_REGEX = /^(.+?)\s*:\s*(.+?)\s*-\s*(.+)$/;

const buildCinemaRowSeatData = (tableNumbers) => {
  const cinemaRows = {};
  const cinemaRowSeats = {};

  tableNumbers.forEach(({ value }) => {
    const match = value.match(CINEMA_ROW_SEAT_REGEX);
    if (!match) return;

    const [cinemaPart, rest] = value.split(': ');
    const [rowPart, seatPart] = rest.split(' - ');
    const cinema = cinemaPart.trim();
    const row = rowPart.trim();
    const seat = seatPart.trim();

    if (!cinemaRows[cinema]) cinemaRows[cinema] = new Set();
    cinemaRows[cinema].add(row);

    const key = `${cinema}|${row}`;
    if (!cinemaRowSeats[key]) cinemaRowSeats[key] = new Set();
    cinemaRowSeats[key].add(seat);
  });

  const cinemas = Object.keys(cinemaRows).sort(numericSort);
  const rowsMap = {};
  const seatsMap = {};

  for (const cinema of cinemas) {
    rowsMap[cinema] = [...cinemaRows[cinema]].sort(numericSort);
  }
  for (const key of Object.keys(cinemaRowSeats)) {
    seatsMap[key] = [...cinemaRowSeats[key]].sort(numericSort);
  }

  return { cinemas, rowsMap, seatsMap };
};

const toOption = (item) => ({ label: item, value: item });

const CinemaRowSeatDropdowns = ({ tableNumbers, value, onChange, hasError }) => {
  const { language } = useDataContext();

  const [cinema, setCinema] = useState(null);
  const [row, setRow] = useState(null);
  const [seat, setSeat] = useState(null);

  const { cinemas, rowsMap, seatsMap } = useMemo(
    () => buildCinemaRowSeatData(tableNumbers),
    [tableNumbers],
  );

  const cinemaOptions = useMemo(() => cinemas.map(toOption), [cinemas]);
  const rowOptions = useMemo(
    () => (cinema ? (rowsMap[cinema.value] || []).map(toOption) : []),
    [cinema, rowsMap],
  );
  const seatOptions = useMemo(
    () => (cinema && row ? (seatsMap[`${cinema.value}|${row.value}`] || []).map(toOption) : []),
    [cinema, row, seatsMap],
  );

  // Sync internal state from external value (e.g. restored data)
  useEffect(() => {
    if (value?.value && !cinema) {
      const [cinemaPart, rest] = value.value.split(': ');
      const [rowPart, seatPart] = rest.split(' - ');
      setCinema(toOption(cinemaPart.trim()));
      setRow(toOption(rowPart.trim()));
      setSeat(toOption(seatPart.trim()));
    }
  }, [value]);

  const handleCinemaChange = (option) => {
    setCinema(option);
    setRow(null);
    setSeat(null);
    onChange(null);
  };

  const handleRowChange = (option) => {
    setRow(option);
    setSeat(null);
    onChange(null);
  };

  const handleSeatChange = (option) => {
    setSeat(option);
    if (cinema && row && option) {
      const formatted = `${cinema.value}: ${row.value} - ${option.value}`;
      const match = tableNumbers.find((t) => t.value === formatted);
      onChange(match || null);
    } else {
      onChange(null);
    }
  };

  const showErrors = hasError && !value;
  const errorMsgStyle = 'text-red-500 text-sm mt-1';

  return (
    <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 space-y-2 lg:space-y-4 border-b border-[#f5f5f5]">
      <div className="flex flex-col sm:flex-row sm:items-center">
        <div className="flex-1 mb-2 sm:!mb-0">
          <label className="block text-sm lg:text-base font-bold">
            {translations[language]?.order_information_cinema_label}
          </label>
        </div>

        <div className="flex-1">
          <GenericSelect
            placeholder={translations[language]?.order_information_cinema_placeholder}
            options={cinemaOptions}
            value={cinema}
            onChange={handleCinemaChange}
            hasError={showErrors && !cinema}
          />
          {showErrors && !cinema && (
            <p className={errorMsgStyle}>
              {translations[language]?.order_information_cinema_error}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center">
        <div className="flex-1 mb-2 sm:!mb-0">
          <label className="block text-sm lg:text-base font-bold">
            {translations[language]?.order_information_row_label}
          </label>
        </div>

        <div className="flex-1">
          <GenericSelect
            placeholder={translations[language]?.order_information_row_placeholder}
            options={rowOptions}
            value={row}
            onChange={handleRowChange}
            isDisabled={!cinema}
            hasError={showErrors && !row}
          />
          {showErrors && !row && (
            <p className={errorMsgStyle}>{translations[language]?.order_information_row_error}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center">
        <div className="flex-1 mb-2 sm:!mb-0">
          <label className="block text-sm lg:text-base font-bold">
            {translations[language]?.order_information_seat_label}
          </label>
        </div>

        <div className="flex-1">
          <GenericSelect
            placeholder={translations[language]?.order_information_seat_placeholder}
            options={seatOptions}
            value={seat}
            onChange={handleSeatChange}
            isDisabled={!row}
            hasError={showErrors && !seat}
          />
          {showErrors && !seat && (
            <p className={errorMsgStyle}>{translations[language]?.order_information_seat_error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CinemaRowSeatDropdowns;
